import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Button from '@/components/ui/Button';
import TokenIcon from '@/components/ui/TokenIcon';
import { useCountdown } from '@/hooks/useCountdown';
import { useSwap } from '@/context/SwapContext';
import { useWallet } from '@/hooks/useWallet';
import TokenSelector from '@/components/swap/TokenSelector';
import { useState, useEffect } from 'react';

const heroImages = [
  '/images/hero-1.jpg',
  '/images/hero-2.jpg',
  '/images/hero-3.jpg'
];

const HeroSection = () => {
  // Set target date (November 1st, 2025 at midnight Toronto time - EDT is UTC-4)
  const timeLeft = useCountdown('2025-11-01T04:00:00Z');
  
  // Wallet and swap hooks
  const { swapState, setFromToken, setFromAmount, getQuote, swapTokens } = useSwap();
  const { isConnected, address, balance, connectWallet, isConnecting } = useWallet();

  // Simple carousel state
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Handle input changes and auto-quote
  useEffect(() => {
    if (swapState.fromAmount && parseFloat(swapState.fromAmount) > 0) {
      const timer = setTimeout(() => {
        getQuote();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [swapState.fromAmount, swapState.fromToken, swapState.toToken, getQuote]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroImages.length);
    }, 6000);

    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32 pb-20">
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2, ease: "linear" }}
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${heroImages[currentIndex]})`,
            }}
          />
        </AnimatePresence>

        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0" />

        {/* Gradient overlay */}
        <div className="absolute inset-0" />
      </div>

      {/* Carousel Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentIndex
              ? 'bg-white shadow-lg scale-110'
              : 'bg-white/50 hover:bg-white/75'
              }`}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-8">
          {/* Left Content */}
          <div className="lg:basis-4/6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl md:text-6xl font-display font-bold text-white mb-6 leading-tight drop-shadow-lg"
              >
                FoodSply Chain<br />
                Marketplace
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-xl text-gray-100 mb-8 max-w-lg mx-auto lg:mx-0 drop-shadow-md"
              >
                Buy direct from verified farmers, farm to fork.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-16"
              >
                <Button size="lg" className="group" style={{ borderRadius: '50px' }} onClick={() => window.location.href = '/waitlist'}>
                  Join Waitlist
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </motion.div>

              {/* Countdown Timer */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-4 gap-2 sm:gap-4 max-w-md mx-auto lg:mx-0"
              >
                {Object.entries(timeLeft).map(([unit, value]) => (
                  <div key={unit} className="text-center">
                    <div 
                      className="backdrop-blur-lg shadow-lg p-2 sm:p-4"
                      style={{
                        background: `linear-gradient(180deg, rgba(0, 74, 19, 0.12) 0%, rgba(239, 156, 0, 0.12) 100%)`,
                        borderTopColor: '#04AD2F',
                        borderLeftColor: '#04AD2F',
                        borderRightColor: '#EF9C00',
                        borderBottomColor: '#EF9C00',
                        borderWidth: '0.5px',
                        borderRadius: '50px'
                      }}
                    >
                      <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white">
                        {value.toString().padStart(2, '0')}
                      </div>
                      <div className="text-[10px] sm:text-xs uppercase text-white/70 font-medium">
                        {unit}
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>

          {/* Right Content - Token Swap Widget */}
          <div className="lg:basis-2/6 mt-8 lg:mt-0 flex justify-center">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="w-full flex justify-center"
            >
              <div 
                className="backdrop-blur-lg shadow-2xl p-8 max-w-md w-full"
                style={{
                  background: `linear-gradient(180deg, rgba(0, 74, 19, 0.12) 0%, rgba(239, 156, 0, 0.12) 100%)`,
                  borderTopColor: '#04AD2F',
                  borderLeftColor: '#04AD2F',
                  borderRightColor: '#EF9C00',
                  borderBottomColor: '#EF9C00',
                  borderWidth: '0.5px',
                  borderRadius: '50px'
                }}
              >
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Token Swap</h3>
                </div>

                <div className="flex mb-5 justify-center">
                  <Button 
                    className="px-10 mb-5 !bg-white !text-foodsply-green !text-gray-900 hover:!bg-gray-50" 
                    size="lg" 
                    style={{ 
                      borderRadius: '50px',
                      borderTopColor: '#04AD2F',
                      borderLeftColor: '#04AD2F',
                      borderRightColor: '#EF9C00',
                      borderBottomColor: '#EF9C00',
                      borderWidth: '0.5px'
                    }}
                    onClick={isConnected ? undefined : connectWallet}
                    disabled={isConnecting}
                  >
                    <img src="/images/plus.svg" alt="" className="w-5 h-5 mr-2" />
                    {isConnecting ? 'Connecting...' : isConnected ? `${address?.slice(0, 6)}...${address?.slice(-4)}` : 'Connect Wallet'}
                  </Button>
                </div>

                                <div className="space-y-4 relative" style={{ zIndex: 1 }}>
                  <div 
                    className="backdrop-blur-lg py-3 px-4 sm:py-4 sm:px-8 relative z-20"
                    style={{
                      background: `linear-gradient(180deg, rgba(0, 74, 19, 0.12) 0%, rgba(239, 156, 0, 0.12) 100%), rgba(255, 255, 255, 0.05)`,
                      borderTopColor: '#04AD2F',
                      borderLeftColor: '#04AD2F',
                      borderRightColor: '#EF9C00',
                      borderBottomColor: '#EF9C00',
                      borderWidth: '0.5px',
                      borderRadius: '50px'
                    }}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs sm:text-sm text-white/70">Give</span>
                      <span className="text-xs sm:text-sm text-white/70">Balance: {isConnected ? balance : '0.00'}</span>
                    </div>
                    <div className="flex justify-between space-x-3">
                      <TokenSelector
                        selectedToken={swapState.fromToken}
                        onTokenSelect={setFromToken}
                        className="flex items-center space-x-1.5 sm:space-x-2 backdrop-blur-sm px-2 py-1.5 sm:px-3 sm:py-2 min-w-fit cursor-pointer"
                        iconSize={16}
                        style={{
                          background: 'linear-gradient(90deg, rgba(4, 173, 47, 0.6) 0%, rgba(0, 96, 24, 0.6) 100%)',
                          borderTopColor: '#04AD2F',
                          borderLeftColor: '#04AD2F',
                          borderRightColor: '#EF9C00',
                          borderBottomColor: '#EF9C00',
                          borderWidth: '0.5px',
                          borderRadius: '50px'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="0.00"
                        value={swapState.fromAmount}
                        onChange={(e) => setFromAmount(e.target.value)}
                        className="w-1/2 ml-auto text-right text-base sm:text-lg font-semibold bg-transparent border-none outline-none text-white placeholder-white/50"
                      />
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <button 
                      className="p-2 bg-primary-100 rounded-full hover:bg-primary-200 transition-colors"
                      onClick={swapTokens}
                    >
                      <ArrowRight className="w-5 h-5 text-primary-600 rotate-90" />
                    </button>
                  </div>

                  <div 
                    className="backdrop-blur-lg py-3 px-4 sm:py-4 sm:px-8 relative z-10"
                    style={{
                      background: `linear-gradient(180deg, rgba(0, 74, 19, 0.12) 0%, rgba(239, 156, 0, 0.12) 100%), rgba(255, 255, 255, 0.05)`,
                      borderTopColor: '#04AD2F',
                      borderLeftColor: '#04AD2F',
                      borderRightColor: '#EF9C00',
                      borderBottomColor: '#EF9C00',
                      borderWidth: '0.5px',
                      borderRadius: '50px'
                    }}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs sm:text-sm text-white/70">Receive</span>
                      <span className="text-xs sm:text-sm text-white/70">Balance: 0.00</span>
                    </div>
                    <div className="flex justify-between space-x-3">
                      <div 
                        className="flex items-center space-x-1.5 sm:space-x-2 backdrop-blur-sm px-2 py-1.5 sm:px-3 sm:py-2 min-w-fit"
                        style={{
                          background: 'linear-gradient(90deg, rgba(4, 173, 47, 0.6) 0%, rgba(0, 96, 24, 0.6) 100%)',
                          borderTopColor: '#04AD2F',
                          borderLeftColor: '#04AD2F',
                          borderRightColor: '#EF9C00',
                          borderBottomColor: '#EF9C00',
                          borderWidth: '0.5px',
                          borderRadius: '50px'
                        }}
                      >
                        <TokenIcon 
                          symbol="FSPY"
                          size={16}
                          className="flex-shrink-0"
                          alt="FSPY Token"
                        />
                        <span className="font-medium text-xs sm:text-sm text-white whitespace-nowrap">FSPY</span>
                      </div>
                      <input
                        type="text"
                        placeholder="0.00"
                        value={swapState.toAmount}
                        readOnly
                        className="w-1/2 ml-auto text-right text-base sm:text-lg font-semibold bg-transparent border-none outline-none text-white placeholder-white/50"
                      />
                    </div>
                  </div>

                  <div className="flex mt-5">
                    <Button 
                      className="px-10 mx-auto mt-5 !bg-white !text-foodsply-green !text-gray-900 hover:!bg-gray-50" 
                      size="lg" 
                      style={{ 
                        borderRadius: '50px',
                        borderTopColor: '#04AD2F',
                        borderLeftColor: '#04AD2F',
                        borderRightColor: '#EF9C00',
                        borderBottomColor: '#EF9C00',
                        borderWidth: '0.5px'
                      }}
                      disabled={!isConnected || !swapState.fromAmount || swapState.isLoading}
                      onClick={() => {
                        if (isConnected && swapState.fromAmount) {
                          alert(`Swapping ${swapState.fromAmount} ${swapState.fromToken} for ${swapState.toAmount} ${swapState.toToken}`);
                        }
                      }}
                    >
                      <img src="/images/wallet.svg" alt="" className="w-5 h-5 mr-2" />
                      {swapState.isLoading ? 'Getting Quote...' : 'Swap'}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;